// Centralized selectors file - exports all memoized selectors
export * from './cartSlice';
export * from './productSlice';
export * from './OrdersSlice';
export * from './locationSlice';
export * from './AuthSlice';
export * from './SettingsSlice';
export * from './partialPaymentsSlice';
export * from './productSortSlice';

// Additional composite selectors that combine data from multiple slices
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from './types';
import { selectCartItems, selectCartTotalAmount } from './cartSlice';
import { selectProductDocs } from './productSlice';
import { selectValidCurrentLocation } from './locationSlice';

// Composite selector: Cart items with product details
export const selectCartItemsWithProductDetails = createSelector(
  [selectCartItems, selectProductDocs],
  (cartItems, products) => {
    return cartItems.map(cartItem => {
      const product = products.find(p => p._id === cartItem.productID);
      return {
        ...cartItem,
        productDetails: product || null,
      };
    });
  },
);

// Composite selector: Cart summary with location info
export const selectCartSummary = createSelector(
  [selectCartItems, selectCartTotalAmount, selectValidCurrentLocation],
  (cartItems, totalAmount, currentLocation) => {
    return {
      itemCount: cartItems.length,
      totalAmount,
      locationName: currentLocation?.bp_location_name || 'Unknown Location',
      hasItems: cartItems.length > 0,
    };
  },
);

// Composite selector: Products available at current location
export const selectProductsAtCurrentLocation = createSelector(
  [selectProductDocs, selectValidCurrentLocation],
  (products, currentLocation) => {
    if (!currentLocation) return products;
    // Filter products by location if needed
    // For now, return all products since location filtering might be handled elsewhere
    return products;
  },
);

// Composite selector: Business dashboard data
export const selectBusinessDashboardData = createSelector(
  [
    (state: RootState) => state.auth.inApp.account.businessInfo,
    (state: RootState) => state.auth.inApp.account.employeeInfo,
    selectValidCurrentLocation,
    selectCartTotalAmount,
  ],
  (businessInfo, employeeInfo, currentLocation, cartTotal) => {
    return {
      businessName: businessInfo?.name || 'Unknown Business',
      employeeName: employeeInfo?.bp_employee_name || 'Unknown Employee',
      locationName: currentLocation?.bp_location_name || 'Unknown Location',
      currentCartTotal: cartTotal,
    };
  },
);
